
import type { MenuPermission } from '../types';

// Define role-based menu permissions
export const menuPermissions: MenuPermission[] = [
  {
    path: '/dashboard',
    label: 'Dashboard',
    icon: 'ri-dashboard-line',
    roles: ['superAdmin', 'admin', 'manager', 'employee', 'trainee', 'client']
  },
  {
    path: '/projects',
    label: 'Projects',
    icon: 'ri-building-line',
    roles: ['superAdmin', 'admin', 'manager', 'employee', 'trainee', 'client']
  },
  {
    path: '/proj-assign',
    label: 'Proj Assign',
    icon: 'ri-user-follow-line',
    roles: ['superAdmin', 'admin', 'manager']
  },
  {
    path: '/tasks',
    label: 'Tasks',
    icon: 'ri-task-line',
    roles: ['superAdmin', 'admin', 'manager', 'employee', 'trainee'],
    subItems: [
      {
        path: '/tasks/manage',
        label: 'Manage Tasks',
        icon: 'ri-settings-3-line',
        roles: ['superAdmin', 'admin', 'manager', 'employee', 'trainee']
      },
      {
        path: '/tasks/daily-entry',
        label: 'Daily Task Entry',
        icon: 'ri-edit-box-line',
        roles: ['superAdmin', 'admin', 'manager', 'employee', 'trainee']
      }
    ]
  },
  {
    path: '/proposals',
    label: 'Proposals',
    icon: 'ri-file-text-line',
    roles: ['superAdmin', 'admin', 'client']
  },
  {
    path: '/files',
    label: 'Files',
    icon: 'ri-folder-line',
    roles: ['superAdmin', 'admin', 'manager', 'employee', 'trainee', 'client']
  },
  {
    path: '/visits',
    label: 'Visits',
    icon: 'ri-map-pin-line',
    roles: ['superAdmin', 'admin', 'manager', 'employee', 'trainee', 'client']
  },
  {
    path: '/payments',
    label: 'Payments',
    icon: 'ri-money-dollar-circle-line',
    roles: ['superAdmin', 'admin', 'client']
  },
  {
    path: '/expenses',
    label: 'Expenses',
    icon: 'ri-wallet-line',
    roles: ['superAdmin', 'admin', 'manager', 'employee', 'trainee']
  },
  {
    path: '/clients',
    label: 'Clients',
    icon: 'ri-user-line',
    roles: ['superAdmin', 'admin', 'manager']
  },
  {
    path: '/employees',
    label: 'Employees',
    icon: 'ri-team-line',
    roles: ['superAdmin', 'admin', 'manager', 'employee']
  },
  {
    path: '/attendance',
    label: 'Attendance',
    icon: 'ri-time-line',
    roles: ['superAdmin', 'admin', 'manager', 'employee', 'trainee']
  },
  {
    path: '/payroll',
    label: 'Payroll',
    icon: 'ri-money-cny-circle-line',
    roles: ['superAdmin', 'admin']
  },
  {
    path: '/activity-log',
    label: 'Activity Log',
    icon: 'ri-history-line',
    roles: ['superAdmin']
  },
  {
    path: '/reports',
    label: 'Reports',
    icon: 'ri-bar-chart-line',
    roles: ['superAdmin']
  },
  {
    path: '/settings',
    label: 'Settings',
    icon: 'ri-settings-line',
    roles: ['superAdmin', 'admin', 'manager', 'employee', 'trainee', 'client'],
    subItems: [
      {
        path: '/settings?tab=general',
        label: 'General Settings',
        icon: 'ri-settings-2-line',
        roles: ['superAdmin', 'admin']
      },
      {
        path: '/settings?tab=theme',
        label: 'Theme Settings',
        icon: 'ri-palette-line',
        roles: ['superAdmin']
      },
      {
        path: '/settings?tab=website',
        label: 'Website Settings',
        icon: 'ri-global-line',
        roles: ['superAdmin']
      },
      {
        path: '/settings?tab=configurations',
        label: 'Configurations',
        icon: 'ri-tools-line',
        roles: ['superAdmin', 'admin']
      },
      {
        path: '/settings?tab=roles',
        label: 'Roles & Permissions',
        icon: 'ri-shield-user-line',
        roles: ['superAdmin']
      },
      {
        path: '/settings?tab=change-password',
        label: 'Change Password',
        icon: 'ri-lock-password-line',
        roles: ['superAdmin', 'admin', 'manager', 'employee', 'trainee', 'client']
      },
      {
        path: '/settings?tab=backup',
        label: 'Backup',
        icon: 'ri-cloud-line',
        roles: ['superAdmin']
      }
    ]
  }
];

// Role definitions with descriptions
export const userRoles = {
  superAdmin: {
    id: 'superAdmin',
    name: 'Super Admin',
    description: 'Complete access to all system features and settings',
    permissions: ['*'] // All permissions
  },
  admin: {
    id: 'admin',
    name: 'Admin',
    description: 'Full access except some super admin features',
    permissions: ['dashboard', 'projects', 'tasks', 'proposals', 'files', 'visits', 'payments', 'expenses', 'clients', 'employees', 'attendance', 'payroll', 'reports', 'settings']
  },
  manager: {
    id: 'manager',
    name: 'Manager',
    description: 'Management access with limited settings',
    permissions: ['dashboard', 'projects', 'proj-assign', 'tasks', 'files', 'visits', 'expenses', 'clients', 'employees', 'attendance', 'settings']
  },
  employee: {
    id: 'employee',
    name: 'Employee',
    description: 'Basic employee access to tasks, files, visits, attendance and settings',
    permissions: ['dashboard', 'projects', 'tasks', 'files', 'visits', 'expenses', 'employees', 'attendance', 'settings']
  },
  trainee: {
    id: 'trainee',
    name: 'Trainee/Intern',
    description: 'Limited access for trainees and interns',
    permissions: ['dashboard', 'projects', 'tasks', 'files', 'visits', 'expenses', 'attendance', 'settings']
  },
  client: {
    id: 'client',
    name: 'Client',
    description: 'Client access with limited menus',
    permissions: ['dashboard', 'projects', 'proposals', 'files', 'visits', 'payments', 'settings']
  }
};

export const normalizeUserRole = (role: string): string => {
  const r = String(role || '').trim();
  const lower = r.toLowerCase();

  if (lower === 'superadmin' || lower === 'super_admin' || lower === 'super-admin') return 'superAdmin';
  if (lower === 'admin') return 'admin';
  if (lower === 'manager') return 'manager';
  if (lower === 'employee') return 'employee';
  if (lower === 'trainee' || lower === 'intern' || lower === 'trainee/intern') return 'trainee';
  if (lower === 'client') return 'client';

  return r;
};

export const isAdminRole = (role: string | null | undefined): boolean => {
  return normalizeUserRole(String(role || '')) === 'admin';
};

export const isSuperAdminRole = (role: string | null | undefined): boolean => {
  return normalizeUserRole(String(role || '')) === 'superAdmin';
};

// Check if user has permission to access a menu item
export const hasPermission = (userRole: string, menuPath: string): boolean => {
  const normalizedRole = normalizeUserRole(userRole);
  if (normalizedRole === 'superAdmin') return true;
  
  // Handle query parameters in path
  const cleanPath = menuPath.split('?')[0];
  
  // Find the main menu item
  const menuItem = menuPermissions.find(item => {
    if (item.path === cleanPath) return true;
    // Check sub-items
    if (item.subItems) {
      return item.subItems.some(subItem => subItem.path.split('?')[0] === cleanPath || subItem.path === menuPath);
    }
    return false;
  });
  
  if (!menuItem) return false;

  // If the request matches the parent menu path exactly, check parent roles.
  // (Important for items like '/settings' where subItems share the same base path.)
  if (menuItem.path === cleanPath && menuPath === cleanPath) {
    return menuItem.roles.includes(normalizedRole as any);
  }
  
  // If it's a sub-item, check the sub-item permissions
  if (menuItem.subItems) {
    const subItem = menuItem.subItems.find(sub => {
      if (sub.path === menuPath) return true;
      // Only fall back to base-path matching when the base path is not the parent path.
      return menuItem.path !== cleanPath && sub.path.split('?')[0] === cleanPath;
    });
    if (subItem) {
      return subItem.roles.includes(normalizedRole as any);
    }
  }
  
  return menuItem.roles.includes(normalizedRole as any);
};

// Get filtered menu items based on user role
export const getFilteredMenuItems = (userRole: string) => {
  const normalizedRole = normalizeUserRole(userRole);
  return menuPermissions.filter(item => {
    if (normalizedRole === 'superAdmin') return true;
    return item.roles.includes(normalizedRole as any);
  }).map(item => {
    if (item.subItems) {
      return {
        ...item,
        subItems: item.subItems.filter(subItem => 
          normalizedRole === 'superAdmin' || subItem.roles.includes(normalizedRole as any)
        )
      };
    }
    return item;
  });
};

type FeatureKey =
  | 'files.client_visibility';

const featureAccess: Record<FeatureKey, { roles: string[] }> = {
  'files.client_visibility': {
    roles: ['superAdmin'],
  },
};

export const hasFeatureAccess = (userRole: string | null | undefined, feature: FeatureKey): boolean => {
  const normalizedRole = normalizeUserRole(String(userRole || ''));
  if (normalizedRole === 'superAdmin') return true;
  const rule = featureAccess[feature];
  return !!rule?.roles?.includes(normalizedRole);
};

// Sample users for demonstration
export const sampleUsers = [
  {
    id: '1',
    name: 'John Doe',
    email: 'john.doe@archpro.com',
    role: 'superAdmin' as const,
    avatar: 'https://readdy.ai/api/search-image?query=professional%20business%20man%20portrait%20in%20modern%20office%20setting%20with%20architectural%20background%2C%20clean%20corporate%20headshot%20style&width=100&height=100&seq=1&orientation=squarish',
    isActive: true,
    createdAt: '2024-01-15T08:00:00Z'
  },
  {
    id: '2',
    name: 'Sarah Wilson',
    email: 'sarah.wilson@archpro.com',
    role: 'admin' as const,
    avatar: 'https://readdy.ai/api/search-image?query=professional%20business%20woman%20portrait%20in%20modern%20office%20setting%20with%20architectural%20background%2C%20clean%20corporate%20headshot%20style&width=100&height=100&seq=2&orientation=squarish',
    isActive: true,
    createdAt: '2024-01-20T09:00:00Z'
  },
  {
    id: '3',
    name: 'Michael Chen',
    email: 'michael.chen@archpro.com',
    role: 'manager' as const,
    avatar: 'https://readdy.ai/api/search-image?query=professional%20business%20man%20manager%20portrait%20in%20modern%20office%20setting%20with%20architectural%20background%2C%20clean%20corporate%20headshot%20style&width=100&height=100&seq=3&orientation=squarish',
    isActive: true,
    createdAt: '2024-02-01T10:00:00Z'
  },
  {
    id: '4',
    name: 'Emily Rodriguez',
    email: 'emily.rodriguez@archpro.com',
    role: 'employee' as const,
    avatar: 'https://readdy.ai/api/search-image?query=professional%20business%20woman%20employee%20portrait%20in%20modern%20office%20setting%20with%20architectural%20background%2C%20clean%20corporate%20headshot%20style&width=100&height=100&seq=4&orientation=squarish',
    isActive: true,
    createdAt: '2024-02-10T11:00:00Z'
  },
  {
    id: '5',
    name: 'David Kumar',
    email: 'david.kumar@archpro.com',
    role: 'trainee' as const,
    avatar: 'https://readdy.ai/api/search-image?query=young%20professional%20trainee%20portrait%20in%20modern%20office%20setting%20with%20architectural%20background%2C%20clean%20corporate%20headshot%20style&width=100&height=100&seq=5&orientation=squarish',
    isActive: true,
    createdAt: '2024-02-15T12:00:00Z'
  }
];
